/**
 * Standardreihenfolge der Mitarbeiterlisten — eine Stelle für beide Seiten.
 *
 * Entscheidung Marie, 10.09.2026:
 *   Mein Team, Schalter Bindung            → höchster Risiko-Score oben
 *   Mein Team, Schalter Wirtschaftlichkeit → höchste Wirtschafts-Ampel oben
 *   Wirtschaftlichkeit, Mitarbeitertabelle → höchste Wirtschafts-Ampel oben
 *
 * Vorher galt auf "Mein Team" in jeder Ansicht die schlechtere der BEIDEN
 * Ampeln. In Armins Team stand dadurch Elias Geigle (Score 31 %) über Michelle
 * Neumann (40 %) — er war wirtschaftlich rot und alphabetisch vorn. In der
 * Bindungs-Ansicht las sich das wie ein Fehler.
 *
 * Nach dem Score zu sortieren passt zu den Ampelpunkten: Die Bindungs-Ampel
 * folgt allein aus dem Score (retention-scoring.ts: ab 45 % Warnung, ab 70 %
 * kritisch). Wer weiter oben steht, hat nie den grüneren Punkt.
 *
 * Die Wirtschafts-Reihenfolge ist dieselbe wie auf der Wirtschaftlichkeits-
 * Seite — dort galt sie schon. Eine Person steht damit an beiden Stellen gleich.
 */
import type { MeinTeamRow, WirtschaftAmpelLevel } from "./api.js";

const AMPEL_RANG: Record<WirtschaftAmpelLevel, number> = { ok: 0, warning: 1, critical: 2 };

const nachName = (a: MeinTeamRow, b: MeinTeamRow) => a.name.localeCompare(b.name, "de");

/**
 * Höchster Risiko-Score zuerst. Ohne Score ans Ende — dazu gehören bestätigte
 * Kündigungen: Sie sind kein Risiko-Fall mehr (Prinzip Marie 11.06.) und
 * tragen ihre eigene Kennzeichnung.
 */
export function nachRisikoScore(a: MeinTeamRow, b: MeinTeamRow): number {
  const x = a.bindung.score;
  const y = b.bindung.score;
  if (x == null && y == null) return nachName(a, b);
  if (x == null) return 1;
  if (y == null) return -1;
  return y - x || nachName(a, b);
}

/** Kritisch vor Warnung vor OK; bei gleicher Ampel der höhere Umsatz 12M zuerst. */
export function nachWirtschaftsAmpel(a: MeinTeamRow, b: MeinTeamRow): number {
  return (
    AMPEL_RANG[b.wirtschaft.level] - AMPEL_RANG[a.wirtschaft.level] ||
    b.wirtschaft.umsatz12m - a.wirtschaft.umsatz12m ||
    nachName(a, b)
  );
}
